import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import type { ArticleData } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article: ArticleData | undefined = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-secondary-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              Article Not Found
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              The article you are looking for does not exist or has been moved.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-all duration-200"
            >
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.heroImage,
    author: { "@type": "Person", name: "VigorBuy Editorial Team" },
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    publisher: { "@type": "Organization", name: "VigorBuy Beauty", url: __VITE_SITE_URL__, logo: { "@type": "ImageObject", url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg" } },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${__VITE_SITE_URL__}/article/${article.slug}` },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${__VITE_SITE_URL__}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: article.title,
        item: `${__VITE_SITE_URL__}/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative w-full h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.heroImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          </div>

          <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
            <span className="inline-block text-xs font-medium text-accent-300 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full mb-4">
              {article.tag} • 2026 Edition
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight">
              {article.title}
              <br />
              <span className="text-white/75 font-light text-lg md:text-2xl">
                {article.subtitle}
              </span>
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/65 max-w-2xl mx-auto">
              {article.description}
            </p>
            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-white/50">
              <span>
                <i className="ri-calendar-line mr-1"></i>Updated: {article.dateModified}
              </span>
              <span>
                <i className="ri-time-line mr-1"></i>{article.readTime}
              </span>
              <span>
                <i className="ri-user-line mr-1"></i>By VigorBuy Editorial Team
              </span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {/* Breadcrumb */}
              <nav className="mb-10 text-xs text-foreground-400">
                <Link
                  to="/"
                  className="hover:text-accent-600 transition-colors duration-200"
                >
                  Home
                </Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-600 font-medium">
                  {article.title}
                </span>
              </nav>

              {/* Article Body */}
              <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-5">
                {article.sections.map((section, sIdx) => (
                  <div key={sIdx}>
                    {section.heading && section.headingLevel === "h2" && (
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 pt-6 pb-2">
                        {section.heading}
                      </h2>
                    )}
                    {section.heading && section.headingLevel === "h3" && (
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 pt-3">
                        {section.heading}
                      </h3>
                    )}

                    {section.paragraphs.map((para, pIdx) => (
                      <p key={pIdx} className="mb-4">
                        {para}
                      </p>
                    ))}

                    {section.table && (
                      <div className="overflow-x-auto rounded-xl border border-background-200/70 my-6">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-background-200/70 bg-secondary-50">
                              {section.table.headers.map((h, hIdx) => (
                                <th
                                  key={hIdx}
                                  className={`px-5 py-3 text-left font-semibold ${
                                    hIdx === 1
                                      ? "text-accent-700"
                                      : hIdx === 0
                                      ? "text-foreground-800"
                                      : "text-foreground-600"
                                  }`}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-background-200/50">
                            {section.table.rows.map((row, rIdx) => (
                              <tr
                                key={rIdx}
                                className={
                                  rIdx % 2 === 0 ? "bg-background-50" : "bg-secondary-50/50"
                                }
                              >
                                {row.map((cell, cIdx) => (
                                  <td
                                    key={cIdx}
                                    className={`px-5 py-3 ${
                                      cIdx === 0
                                        ? "font-medium text-foreground-700"
                                        : cIdx === 1
                                        ? "text-accent-700"
                                        : "text-foreground-500"
                                    }`}
                                  >
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}

                {/* Internal Link to Home */}
                <div className="pt-6 mt-6 border-t border-background-200/70">
                  <p className="text-sm text-foreground-500">
                    Want to explore all categories?{" "}
                    <Link
                      to="/"
                      className="text-accent-600 font-medium hover:text-accent-700 underline transition-colors duration-200"
                    >
                      Return to the Vigorbuy homepage
                    </Link>{" "}
                    to browse the complete Vigorbuy Spreadsheet resource. Or{" "}
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-accent-600 font-medium hover:text-accent-700 underline transition-colors duration-200"
                    >
                      visit the main spreadsheet
                    </a>{" "}
                    to start exploring products directly.
                  </p>
                </div>
              </div>

              {/* Back to Home CTA */}
              <div className="mt-16 pt-8 border-t border-background-200/70 text-center">
                <p className="text-sm text-foreground-500 mb-4">
                  Found this article helpful? Explore more resources and browse all 14 categories on the homepage.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line"></i>
                  Back to Vigorbuy Homepage
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}